import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowUpRight,
  ArrowDown,
  Sparkles,
  Globe2,
  Check,
  Zap,
  Star,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import useLenis from "@/hooks/useLenis";
import UnevoHeader from "@/components/unevo/UnevoHeader";
import Hero3D from "@/components/unevo/Hero3D";
import ServiceCard from "@/components/unevo/ServiceCard";
import UnevoContact from "@/components/unevo/UnevoContact";
import {
  UNEVO,
  SERVICES,
  PROCESS_STEPS,
  PACKAGES,
  STATS,
  OFFICES,
} from "@/data-unevo";

// ─── Kinetic hero copy reveal ──────────────────────────────────────────────
const HEADLINE_LINES = ["Elevate", "Everything.", ""];

// ─── Animated counter ──────────────────────────────────────────────────────
function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, to, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.floor(v)),
    });
    return () => c.stop();
  }, [inView, to]);
  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
}

// ─── HERO ──────────────────────────────────────────────────────────────────
function UnevoHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const canvasY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="unevo-hero"
      className="relative min-h-[100vh] overflow-hidden bg-[#0B0F1A] text-white"
    >
      {/* 3D canvas layer */}
      <motion.div style={{ y: canvasY }} className="absolute inset-0">
        <Hero3D />
      </motion.div>

      {/* radial darken overlay so text stays readable */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0B0F1A]/95 via-[#0B0F1A]/60 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0B0F1A] to-transparent" />

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto flex min-h-[100vh] max-w-7xl flex-col justify-center px-6 pt-32 pb-24 md:px-12"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-white/70 backdrop-blur"
        >
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-green" />
          Global Business Solutions · Germany · USA · India
        </motion.div>

        <h1 className="font-heading text-6xl font-extrabold leading-[0.9] tracking-tight sm:text-7xl lg:text-[8rem]">
          {HEADLINE_LINES.slice(0, 2).map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.15 }}
                className="block"
              >
                {i === 1 ? <span className="text-vibrant-orange">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
        >
          Unevo Consultant is a global business solutions company powering growth through{" "}
          <span className="font-semibold text-white">tech, talent and strategy</span> — from
          software and websites, to RPO, BPO, PR and digital marketing.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <a
            href="#contact"
            data-testid="unevo-hero-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-vibrant-orange px-8 py-4 text-base font-semibold text-white shadow-xl shadow-vibrant-orange/30 transition-all duration-300 hover:-translate-y-1 hover:bg-orange-600"
          >
            Start a Project
            <ArrowUpRight size={20} className="transition-transform group-hover:rotate-45" />
          </a>
          <a
            href="#services"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-white hover:text-deep-trust-blue"
          >
            Explore Services
          </a>
        </motion.div>

        <motion.a
          href="#services"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-16 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/50 hover:text-white"
        >
          <ArrowDown size={14} className="animate-bounce" /> Scroll to see how we help
        </motion.a>
      </motion.div>
    </section>
  );
}

// ─── MARQUEE TAGLINE ───────────────────────────────────────────────────────
function TaglineMarquee() {
  const words = ["Tech.", "Talent.", "Strategy.", "Growth.", "Impact.", "Elevate Everything."];
  return (
    <section className="border-y border-white/10 bg-[#0B0F1A] py-6" data-testid="unevo-marquee">
      <Marquee speed={45} gradient={false} autoFill pauseOnHover>
        {words.map((w, i) => (
          <span key={i} className="mx-10 inline-flex items-center gap-8 font-heading text-4xl font-extrabold uppercase tracking-tight text-white/80 md:text-6xl">
            <Star size={20} className="text-vibrant-orange" />
            {w}
          </span>
        ))}
      </Marquee>
    </section>
  );
}

// ─── ABOUT ─────────────────────────────────────────────────────────────────
function About() {
  return (
    <section data-testid="unevo-about" className="relative bg-[#0B0F1A] py-24 text-white md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-green">About Unevo</span>
            <h2 className="mt-3 font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
              Powering growth
              <span className="block text-vibrant-orange">Through tech, talent & strategy.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base leading-relaxed text-white/70 md:text-lg">
              {UNEVO.intro}
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                { icon: Zap, label: "Speed of a startup" },
                { icon: Sparkles, label: "Craft of an agency" },
                { icon: Globe2, label: "Reach of a global firm" },
                { icon: Check, label: "Results you can measure" },
              ].map((b, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
                >
                  <b.icon size={18} className="text-vibrant-orange" />
                  <p className="mt-3 text-xs leading-snug text-white/70">{b.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── SERVICES ──────────────────────────────────────────────────────────────
function Services() {
  return (
    <section id="services" data-testid="unevo-services" className="relative bg-[#0B0F1A] py-24 text-white md:py-32">
      <div className="pointer-events-none absolute -left-32 top-20 h-96 w-96 rounded-full bg-vibrant-orange/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-emerald-green/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        <div className="mb-16 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-green">
              What we do
            </span>
            <h2 className="mt-3 font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
              Seven capabilities.
              <span className="block text-vibrant-orange">One growth partner.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm text-white/60 md:text-base">
            Tilt every card — every service is a full team, not a freelancer plug-in. Pick one or bundle them.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PROCESS ───────────────────────────────────────────────────────────────
function Process() {
  return (
    <section id="process" data-testid="unevo-process" className="bg-light-gray py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="mb-14 max-w-2xl">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-green">
            How we work
          </span>
          <h2 className="mt-3 font-heading text-4xl font-extrabold leading-tight tracking-tight text-deep-trust-blue sm:text-5xl">
            A four-step method that keeps every project on the rails.
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PROCESS_STEPS.map((step, i) => (
            <motion.div
              key={step.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className="group relative rounded-3xl border border-deep-trust-blue/10 bg-white p-7 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-deep-trust-blue/10"
            >
              <span className="font-heading text-6xl font-extrabold leading-none text-stroke-orange">
                {step.n}
              </span>
              <h3 className="mt-4 font-heading text-xl font-bold tracking-tight text-deep-trust-blue">
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-deep-trust-blue/65">{step.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PACKAGES ──────────────────────────────────────────────────────────────
function Packages() {
  return (
    <section id="packages" data-testid="unevo-packages" className="bg-light-gray pb-24 md:pb-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="mb-14 max-w-2xl">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-green">
            Website packages
          </span>
          <h2 className="mt-3 font-heading text-4xl font-extrabold leading-tight tracking-tight text-deep-trust-blue sm:text-5xl">
            Pricing that empowers your brand.
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {PACKAGES.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: i * 0.09, duration: 0.6 }}
              data-testid={`unevo-package-${p.id}`}
              className={`relative flex flex-col rounded-3xl border p-8 transition-all duration-500 hover:-translate-y-2 ${
                p.highlight
                  ? "border-vibrant-orange/40 bg-deep-trust-blue text-white shadow-2xl shadow-deep-trust-blue/30"
                  : "border-deep-trust-blue/10 bg-white text-deep-trust-blue hover:shadow-xl"
              }`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-8 rounded-full bg-vibrant-orange px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">
                  Most popular
                </span>
              )}
              <span className={`text-xs font-semibold uppercase tracking-widest ${p.highlight ? "text-emerald-green" : "text-emerald-green"}`}>
                {p.tier}
              </span>
              <h3 className="mt-2 font-heading text-3xl font-extrabold tracking-tight">{p.title}</h3>
              <p className="mt-1 font-heading text-lg text-vibrant-orange">{p.price}</p>

              <ul className={`mt-6 flex-1 space-y-3 text-sm ${p.highlight ? "text-white/75" : "text-deep-trust-blue/70"}`}>
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={16} className="mt-0.5 shrink-0 text-emerald-green" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all duration-300 ${
                  p.highlight
                    ? "bg-vibrant-orange text-white hover:bg-orange-600"
                    : "bg-deep-trust-blue text-white hover:bg-deep-trust-blue/90"
                }`}
              >
                Get started
                <ArrowUpRight size={16} />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── STATS ─────────────────────────────────────────────────────────────────
function StatsSection() {
  return (
    <section data-testid="unevo-stats" className="relative overflow-hidden bg-deep-trust-blue py-20 text-white md:py-24">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="border-l-2 border-vibrant-orange/40 pl-4"
            >
              <div className="font-heading text-5xl font-extrabold tracking-tight text-vibrant-orange sm:text-6xl">
                <Counter to={s.n} suffix={s.suffix} />
              </div>
              <p className="mt-2 text-sm text-white/60">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── GLOBAL PRESENCE ───────────────────────────────────────────────────────
function GlobalPresence() {
  return (
    <section id="global" data-testid="unevo-global" className="bg-[#0B0F1A] py-24 text-white md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="mb-14 max-w-2xl">
          <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-emerald-green">
            <Globe2 size={16} /> Global Presence
          </span>
          <h2 className="mt-3 font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            Six offices.
            <span className="block text-vibrant-orange">Three continents.</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {OFFICES.map((o, i) => (
            <motion.div
              key={o.city}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ delay: i * 0.06, duration: 0.5 }}
              className="group rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-all hover:-translate-y-1 hover:border-emerald-green/40"
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-green">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-green" />
                {o.country}
              </div>
              <h3 className="mt-3 font-heading text-lg font-bold">{o.city}</h3>
              <p className="mt-1 text-xs text-white/50">{o.role}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FOOTER ────────────────────────────────────────────────────────────────
function UnevoFooter() {
  return (
    <footer data-testid="unevo-footer" className="relative overflow-hidden bg-[#0B0F1A] pt-16 text-white">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 gap-10 border-t border-white/10 pt-14 md:grid-cols-12">
          <div className="md:col-span-5">
            <a href="#home" className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-xl border border-vibrant-orange/40 bg-vibrant-orange/10 font-heading text-lg font-black text-vibrant-orange">U</span>
              <span className="font-heading text-xl font-extrabold">Unevo<span className="text-vibrant-orange">.</span></span>
            </a>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/60">
              Powering growth for startups and enterprises through tech, talent and strategy — across Germany, USA and India.
            </p>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-heading text-sm font-semibold uppercase tracking-widest text-white/40">Company</h4>
            <ul className="mt-5 space-y-3">
              <li><a href="#services" className="text-sm text-white/70 hover:text-emerald-green">Services</a></li>
              <li><a href="#process" className="text-sm text-white/70 hover:text-emerald-green">Process</a></li>
              <li><a href="#packages" className="text-sm text-white/70 hover:text-emerald-green">Packages</a></li>
              <li><a href="#global" className="text-sm text-white/70 hover:text-emerald-green">Global</a></li>
              <li><a href="/unnati-ai" className="text-sm text-white/70 hover:text-emerald-green">Unnati AI ↗</a></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-heading text-sm font-semibold uppercase tracking-widest text-white/40">Contact</h4>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              {UNEVO.emails.map((e) => (
                <li key={e}><a href={`mailto:${e}`} className="hover:text-emerald-green">{e}</a></li>
              ))}
              <li className="text-white/60">{UNEVO.headOffice}</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-white/10 py-8 md:flex-row">
          <p className="text-xs text-white/50">© {new Date().getFullYear()} Unevo Consultant Pvt. Ltd. All rights reserved.</p>
          <p className="text-xs text-white/50">Think Smart. Scale Fast. Choose Unevo.</p>
        </div>
      </div>

      {/* massive brand hit */}
      <div className="select-none overflow-hidden">
        <p className="translate-y-4 text-center font-heading text-[22vw] font-extrabold leading-none tracking-tighter text-white/[0.04]">
          UNEVO
        </p>
      </div>
    </footer>
  );
}

// ─── FLOATING WHATSAPP ─────────────────────────────────────────────────────
function WhatsAppFloat() {
  const href = `https://wa.me/${UNEVO.whatsapp}?text=${encodeURIComponent(
    "Hi Unevo, I would like a quote."
  )}`;
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="unevo-whatsapp-btn"
      aria-label="WhatsApp Unevo"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 1.6, type: "spring", stiffness: 200, damping: 15 }}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.94 }}
      className="pulse-ring fixed bottom-6 right-6 z-40 grid h-14 w-14 place-items-center rounded-full bg-emerald-green text-white shadow-xl shadow-emerald-green/30"
    >
      <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </motion.a>
  );
}

// ─── PAGE ──────────────────────────────────────────────────────────────────
export default function UnevoHome() {
  useLenis();
  return (
    <div className="bg-[#0B0F1A] text-white font-body antialiased">
      <UnevoHeader />
      <main>
        <UnevoHero />
        <TaglineMarquee />
        <About />
        <Services />
        <Process />
        <Packages />
        <StatsSection />
        <GlobalPresence />
        <UnevoContact />
      </main>
      <UnevoFooter />
      <WhatsAppFloat />
      <Toaster position="top-center" richColors />
    </div>
  );
}
